import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export * from "./signaloidClient/pipelines";
export * from "./signaloidClient/tiers";
export * from "./signaloidClient/projects"; // * Deprecated

/** Local extension used by the 401 retry interceptor's retry counter. */
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
	retryCount?: number;
}

/** Subset of the AWS Amplify v6 JWT shape consumed by the 401 retry interceptor. */
type JwtLike = {
	payload: { exp?: number };
	toString(): string;
};

type RefreshTokenCallback = () => Promise<JwtLike | undefined>;
type SessionLostCallback = () => Promise<void>;

/*
 *	Signaloid API client.
 *
 *	Must set `Authorization` header to identity token of user.
 *	Default `sam local start-api` port is 3000 so it will clash with the
 *	backend unless overriden as `sam local start-api port --port 3001`.
 */
export const signaloidApiClient = axios.create({
	baseURL: import.meta.env.VITE_SIGNALOID_API_URL || "https://127.0.0.1:3001",
	timeout: 10000 /* 10 seconds */,
});

function registerAccessToken(accessToken: string) {
	backendClient.defaults.headers["Authorization"] = `Bearer ${accessToken}`;
}

function registerIdToken(idToken: string) {
	signaloidApiClient.defaults.headers["Authorization"] = `Bearer ${idToken}`;
}

export async function registerTokens(accessTokenPromise: Promise<string>, idTokenPromise: Promise<string>) {
	try {
		const accessToken = await accessTokenPromise;
		registerAccessToken(accessToken);
	} catch (e) {
		console.log("registerTokens - registerAccessToken", e);
		return;
	}
	try {
		const idToken = await idTokenPromise;
		registerIdToken(idToken);
	} catch (e) {
		console.log("registerTokens - registerIdToken", e);
		return;
	}
}

export const backendClient = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_API_URL || "https://127.0.0.1:3000",
	timeout: 300000 /* 300 seconds */,
});

export function updateBackendClientBaseURL(newBaseURL: string) {
	backendClient.defaults.baseURL = newBaseURL;
}

async function rejectedRequestPromiseInterceptor(
	err: AxiosError,
	axiosInstance: AxiosInstance,
	refreshTokenCallback: RefreshTokenCallback,
	onSessionLost?: SessionLostCallback,
) {
	if (err?.response?.status === 401) {
		console.log("Intercepting '401 Unauthorized' response...");

		const config = err.config as CustomAxiosRequestConfig;

		if (config.retryCount == null) {
			config.retryCount = 0;
		}

		if (config.retryCount >= 3) {
			console.error("Retry limit reached");
			return Promise.reject(err);
		}

		config.retryCount += 1;

		const delayInMs = 100 * config.retryCount;
		await new Promise((resolve) => setTimeout(resolve, delayInMs));

		try {
			/*
			 *  Use the per-instance callback so signaloidApiClient gets the ID
			 *  token and backendClient gets the access token. Hardcoding to
			 *  accessToken here would re-401 every signaloidApiClient retry.
			 */
			const token = await refreshTokenCallback();

			/*
			 *  Defensive: bail before retrying if the refreshed token is
			 *  somehow already expired.
			 */
			const unixTime = Math.floor(Date.now() / 1000);
			const expiration = token?.payload?.exp ?? 0;
			if (unixTime > expiration) {
				console.error("User authorization expired");
				throw new Error("Unauthorized");
			}

			/*
			 *  There exists a valid session now.
			 *  Set the default header to the freshly refreshed token.
			 */
			const tokenString = token?.toString();
			axiosInstance.defaults.headers["Authorization"] = `Bearer ${tokenString}`;
			if (config == undefined) {
				return Promise.reject(err);
			}

			// Ensure headers is initialized
			if (!config.headers) {
				config.headers = {};
			}

			config.headers["Authorization"] = `Bearer ${tokenString}`;
			/*
			 *  Retry the request that failed.
			 */
			return axiosInstance(config);
		} catch (reason) {
			console.error("User is not authenticated:", reason);
			// Session refresh failed after a 401 (e.g. idle logout). Redirect
			// to login so the user isn't left on a page whose API calls will
			// silently fail until they manually refresh.
			if (onSessionLost) {
				await onSessionLost();
			} else {
				window.location.href = "/login";
			}
			throw new Error("Unauthorized");
		}
	}
	return Promise.reject(err);
}

function registerSignaloidApiTokenRefreshInterceptor(
	refreshIdTokenCallback: RefreshTokenCallback,
	onSessionLost?: SessionLostCallback,
) {
	signaloidApiClient.interceptors.response.use(undefined, (err: AxiosError) =>
		rejectedRequestPromiseInterceptor(err, signaloidApiClient, refreshIdTokenCallback, onSessionLost),
	);
}

function registerBackendTokenRefreshInterceptor(
	refreshAccessTokenCallback: RefreshTokenCallback,
	onSessionLost?: SessionLostCallback,
) {
	backendClient.interceptors.response.use(undefined, (err: AxiosError) =>
		rejectedRequestPromiseInterceptor(err, backendClient, refreshAccessTokenCallback, onSessionLost),
	);
}

export function registerRefreshTokenCallbacks(
	refreshAccessTokenCallback: RefreshTokenCallback,
	refreshIdTokenCallback: RefreshTokenCallback,
	onSessionLost?: SessionLostCallback,
) {
	registerSignaloidApiTokenRefreshInterceptor(refreshIdTokenCallback, onSessionLost);
	registerBackendTokenRefreshInterceptor(refreshAccessTokenCallback, onSessionLost);
}

/*
 * Stripe checkout
 */
export function getStripeCheckoutPublishableKey() {
	return signaloidApiClient.get("/checkout-pk");
}

export function postCheckoutSession(priceID: string) {
	return signaloidApiClient.post("/checkout-session", {
		priceId: priceID,
	});
}

export function postPortalSession() {
	return signaloidApiClient.post("/portal-session", "");
}

export function joinWaitlist(formContents: string) {
	return signaloidApiClient.post("/utils/waitlist", formContents);
}

/**
 * Get dynamic content.
 * @param contentID The ID of the dynamic content.
 * @returns {Promise<AxiosResponse<any>>}
 */
// TODO: Warning: Not listed in the docs. Is there an endpoint for this?
export function getDynamicContent(contentID: string): Promise<AxiosResponse<any>> {
	return backendClient.get(`/dynamic-content/${contentID}`);
}
